import Skeleton from 'react-loading-skeleton';
import { AIECGViewer, ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import { getSelectedEventType } from '@/entities/event';
import { RHYTHM_EVENT_TAB_ID } from '@/entities/report';
import useBookmarkEcgViewerWrapper from './useBookmarkEcgViewerWrapper';

const configChart = {
  isDisplayScrollbar: true,
  isEdit: true,
};

export type BookmarkEcgViewerWrapperProps = {
  ecgDataMap: any;
  studyId: string;
  profileId: string;
  timezoneOffset: number;
  isProcessBeatEvent?: boolean;
  handleClickArrowButton: (isNext: boolean) => void;
};

/**
 * Renders the ECG viewer for the bookmark view of the ECG events tab. Shows a three-part skeleton while the strip, heart-rate chart or its options are loading or the ECG object has no samples, otherwise mounts AIECGViewer with rhythm-event canvas ids, arrow scrolling, capture mode, pick-date, event and bookmark indicators, enabling AR beat mode unless a date is being picked.
 *
 * @param props - Viewer inputs carrying the ECG data map, study and profile ids, timezone offset and the beat-event processing flag.
 * @returns The bookmark ECG viewer, or a skeleton while its data loads.
 */
const BookmarkEcgViewerWrapper = ({
  ecgDataMap,
  studyId,
  profileId,
  timezoneOffset,
  isProcessBeatEvent,
  handleClickArrowButton,
}: BookmarkEcgViewerWrapperProps) => {
  const {
    isLoading,
    isLoadingHrChart,
    isLoadingHrChartOptions,
    ecgObject,
    startStripEpoch,
    ecgChartRef,
    pickDateAction,
    isPickingDate,
    aiChannel,
    isDisableNextBtn,
    isDisablePrevBtn,
    selectedEvent,
    eventIndicator,
    bookmarkIndicators,
    handlePickDate,
    getCaptureMode,
    handleCaptureImages,
    clickArrowButtonHandle,
    handleUpdateBeat,
  } = useBookmarkEcgViewerWrapper({
    ecgDataMap,
    studyId,
    profileId,
    timezoneOffset,
    handleClickArrowButton,
  });

  if (
    isLoading ||
    isLoadingHrChartOptions ||
    isLoadingHrChart ||
    !ecgObject?.dataSamples?.length ||
    !startStripEpoch.current
  ) {
    return (
      <div id="ecg-viewer" className="ecg-viewer-container">
        <div className="ecg-viewer-skeleton-container">
          <div className="scroller-container">
            <Skeleton height="100%" />
          </div>
          <div className="chart-container">
            <Skeleton height="100%" />
          </div>
          <div className="menu-container">
            <Skeleton height="100%" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="ecg-viewer" className="ecg-viewer-container">
      <AIECGViewer
        ref={ecgChartRef}
        idComponents={{
          canvasBackground: 'cvBgRhythmEvents',
          exportGridCanvas: 'expGridCvRhythmEvents',
          canvasChart: 'cvChartRhythmEvents',
          canvasObject: 'cvObjRhythmEvents',
          canvasScroller: 'cvScrollerRhythmEvents',
          contextMenu: 'contextMenuRhythmEvents',
          tooltip: 'tooltipRhythmEvents',
          tab: RHYTHM_EVENT_TAB_ID,
        }}
        ecg={ecgObject}
        ecgDataMap={ecgDataMap}
        configChart={configChart}
        isAppliedAI
        isDisableAIAssist
        isHideFineGridMenu
        isHideMarkASampleMenu
        hasArrowButtonScroller
        hasUpdateBeatContextMenu
        handlePickDate={handlePickDate}
        pickDateAction={pickDateAction}
        eventIndicator={eventIndicator}
        bookmarkIndicators={bookmarkIndicators}
        captureMode={getCaptureMode()}
        handleCaptureImages={handleCaptureImages}
        handleClickArrowButton={clickArrowButtonHandle}
        handleUpdateBeat={handleUpdateBeat}
        timeObject={{
          startMoment: startStripEpoch.current,
          timezoneOffset,
        }}
        isInitialARBeatMode={
          !isPickingDate && (isProcessBeatEvent || ECG_VIEWER_CONFIG_STORE.isInitialARBeatMode)
        }
        aiChannel={aiChannel}
        isDisableNextBtn={isDisableNextBtn}
        isDisablePrevBtn={isDisablePrevBtn}
        studyInfoObject={{
          studyId,
          profileId,
          type: getSelectedEventType(selectedEvent),
        }}
      />
    </div>
  );
};

export default BookmarkEcgViewerWrapper;
